/**
 * Everything the mail page needs from drafts, in one place: which draft list is
 * open, its rows, what the rows' menu does, opening a draft in the composer,
 * and scheduling the composer's letter once the confirmation says yes.
 *
 * A hook of its own because `EmailSection` is already the biggest file on this
 * desk; the section only swaps the panes, passes the rail its switch, and
 * renders `modals`.
 */
import { useCallback, useEffect, useState } from "react";
import { useI18n, useT } from "../../../../i18n/I18nProvider";
import {
  deleteDraft,
  getDraft,
  listDrafts,
  scheduleDraft,
  sendDraftNow,
  unscheduleDraft,
  type DraftListItem,
} from "../../../../lib/mailDrafts";
import { useToast } from "../../../ui";
import { ConfirmModal } from "../../_shared/ConfirmModal";
import type { ComposerDraft, ComposerSendExtra } from "../Composer";
import { DraftItems } from "./DraftItems";
import type { DraftKind } from "./DraftList";
import { draftRequestError } from "./draftErrors";
import { formatSendTime } from "./draftTime";
import { ScheduleModal } from "./ScheduleModal";

export function useDraftsView({
  openComposer,
  navigate,
  refreshStats,
  shown,
  onLeave,
}: {
  openComposer: (draft: ComposerDraft) => void;
  navigate: (to: string) => void;
  /** The rail's counts, which every change here moves. */
  refreshStats: () => void;
  /**
   * The list on screen, decided by the rail: which kind, and whose mailbox's
   * letters. Null while the panes show something else.
   */
  shown: { kind: DraftKind; mailbox: string } | null;
  /** Leave the list for the conversation a reply draft belongs to. */
  onLeave: () => void;
}) {
  const t = useT();
  const { locale } = useI18n();
  const toast = useToast();
  const kind = shown?.kind ?? null;
  const mailbox = shown?.mailbox;
  const [rows, setRows] = useState<DraftListItem[]>([]);
  const [busy, setBusy] = useState(false);
  const [deleting, setDeleting] = useState<DraftListItem | null>(null);
  const [timing, setTiming] = useState<DraftListItem | null>(null);

  const load = useCallback(async () => {
    if (!kind) return;
    setBusy(true);
    try {
      setRows((await listDrafts(kind, mailbox)).rows);
    } catch {
      setRows([]);
    } finally {
      setBusy(false);
    }
  }, [kind, mailbox]);

  useEffect(() => {
    if (kind) void load();
    else setRows([]);
  }, [kind, load]);

  /** After anything that moves a draft between lists, or out of them. */
  const changed = useCallback(() => {
    refreshStats();
    void load();
  }, [refreshStats, load]);

  /**
   * Continue a draft in the composer. A reply opens its conversation as well:
   * the To line and the quoted context both come off the thread, and a reply
   * written with its thread out of sight is a reply written blind.
   */
  async function open(row: DraftListItem) {
    try {
      const d = await getDraft(row.id);
      if (d.mode === "reply" && d.threadId) {
        onLeave();
        navigate(`/email/${d.threadId}`);
      }
      openComposer({
        mode: d.mode,
        threadId: d.threadId ?? undefined,
        to: d.to,
        subject: d.subject,
        body: "",
        saved: {
          id: d.id,
          cc: d.cc,
          bcc: d.bcc,
          bodyHtml: d.bodyHtml,
          signature: d.signatureSet ? d.signatureId : undefined,
          fromAddress: d.fromAddress ?? undefined,
          attachments: d.attachments,
        },
      });
    } catch (err) {
      toast(draftRequestError(t, err));
      void load();
    }
  }

  async function sendNow(row: DraftListItem) {
    try {
      await sendDraftNow(row.id);
      toast(t("compose.sent"));
    } catch (err) {
      toast(draftRequestError(t, err));
    }
    changed();
  }

  async function unschedule(row: DraftListItem) {
    try {
      await unscheduleDraft(row.id);
      toast(t("drafts.unscheduled"));
    } catch (err) {
      toast(draftRequestError(t, err));
    }
    changed();
  }

  async function reschedule(row: DraftListItem, at: string) {
    try {
      await scheduleDraft(row.id, at);
      toast(t("drafts.scheduledToast", { when: formatSendTime(at, locale) }));
    } catch (err) {
      toast(draftRequestError(t, err));
    }
    changed();
  }

  /**
   * The composer's letter, scheduled - called once the send confirmation has
   * been accepted. The last few keystrokes are saved first, so the letter that
   * goes at nine is the letter that was on screen, not the one saved a second
   * before it. Throws when the server refuses, for the caller to report.
   */
  async function scheduleLetter(extra: ComposerSendExtra): Promise<void> {
    if (!extra.scheduleAt) return;
    const id = await extra.draft.flush();
    if (!id) throw new Error("draft_not_saved");
    await scheduleDraft(id, extra.scheduleAt);
    extra.draft.stop();
    toast(t("drafts.scheduledToast", { when: formatSendTime(extra.scheduleAt, locale) }));
    changed();
  }

  const renderMenu = (row: DraftListItem, close: () => void) => (
    <DraftItems
      row={row}
      close={close}
      onOpen={(r) => void open(r)}
      onSendNow={(r) => void sendNow(r)}
      onReschedule={setTiming}
      onUnschedule={(r) => void unschedule(r)}
      onDelete={setDeleting}
    />
  );

  const modals = (
    <>
      {timing ? (
        <ScheduleModal
          initial={timing.scheduledAt}
          onClose={() => setTiming(null)}
          onPick={(iso) => {
            const row = timing;
            setTiming(null);
            void reschedule(row, iso);
          }}
        />
      ) : null}

      {deleting ? (
        <ConfirmModal
          title={t("drafts.discardTitle")}
          body={t("drafts.discardBody")}
          confirmLabel={t("drafts.discard")}
          cancelLabel={t("common.cancel")}
          onConfirm={async () => {
            try {
              await deleteDraft(deleting.id);
              toast(t("drafts.discarded"));
            } catch (err) {
              toast(draftRequestError(t, err));
            }
            setDeleting(null);
            changed();
          }}
          onClose={() => setDeleting(null)}
        />
      ) : null}
    </>
  );

  return { kind, rows, busy, load, open, renderMenu, modals, scheduleLetter, changed };
}
